import { type CreateUpsellDealDto, type CreateUpsellDealResponseDto, CrmRequester, type ProductPriceDto, type ProductReferenceDto } from '@cobliteam/api-lib';
import { Environment } from '@cobliteam/react-environments';
import Alert from '@cobliteam/ui/components/Alert';
import { useMutation, useQuery } from '@tanstack/react-query';

export const UPSELL_MODAL = 'UPSELL_MODAL';
export const UPSELL_MODAL_PRODUCT_LIST_QUERY_KEY = 'SCHEDULE_PRODUCT_LIST';
export const UPSELL_MODAL_PRICES_QUERY_KEY = 'SCHEDULE_UPSELL_PRICES';

const env = Environment.getInstance().getEnvironment();

// Catalog, prices and deal creation are served by geococcyx-velox behind the main
// gateway (host = apiHost). Company data (address + mrr) comes from the contracts
// GraphQL query already loaded by the company dropdown (useMultipleContractMenu).
const getCrmRequester = () => new CrmRequester({ host: env.apiHost });

const useUpsellModal = () => {
  // Product catalog (replaces the legacy efop /products route).
  const getProductReferences = () => {
    return useQuery<ProductReferenceDto[]>({
      queryKey: [UPSELL_MODAL, UPSELL_MODAL_PRODUCT_LIST_QUERY_KEY],
      queryFn: () => getCrmRequester().getProductReferences(),
    });
  };

  // Priced references for the fleet size (recurrent + membership base prices). Replaces
  // the legacy /upsell/product/calculate polling — totals are computed client-side.
  const useGetProductPrices = (fleetSize: number, enabled: boolean) => {
    return useQuery<ProductPriceDto[]>({
      queryKey: [UPSELL_MODAL, UPSELL_MODAL_PRICES_QUERY_KEY, fleetSize],
      queryFn: () => getCrmRequester().getProductPrices(fleetSize),
      enabled: enabled && fleetSize > 0,
    });
  };

  // Creates the deal (already filled) at checkout — replaces the efop POST /upsell/deal
  // and the PATCH /upsell/deal/stage calls.
  const useCreateUpsellDeal = () => {
    return useMutation<CreateUpsellDealResponseDto, unknown, CreateUpsellDealDto>({
      mutationFn: async (deal) => getCrmRequester().createUpsellDeal(deal),
      onError: () => {
        Alert.error('Falha ao criar o pedido de upsell.');
      },
    });
  };

  return {
    getProductReferences,
    useGetProductPrices,
    useCreateUpsellDeal,
  };
};
export default useUpsellModal;
